import { useEffect, useRef, useState } from "react";

/**
 * Compact multi-select for table column filters.
 * Empty `value` means no filter (show all rows for that dimension).
 */
export default function TableColumnMultiSelectFilter({
  options,
  value,
  onChange,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function toggle(id) {
    onChange(
      value.includes(id) ? value.filter((v) => v !== id) : [...value, id]
    );
  }

  const label = value.length === 0 ? "All" : `${value.length} selected`;

  return (
    <div ref={rootRef} style={{ position: "relative", minWidth: 88 }}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          padding: "4px 8px",
          border: "1px solid #d1d5db",
          borderRadius: 6,
          fontSize: 11,
          textAlign: "left",
          background: value.length ? "#eff6ff" : "#fff",
          cursor: "pointer",
          fontWeight: 500,
        }}
      >
        {label} ▾
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            minWidth: 200,
            maxWidth: 280,
            border: "1px solid #d1d5db",
            borderRadius: 8,
            background: "#fff",
            zIndex: 40,
            boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              gap: 6,
              padding: "6px 8px",
              borderBottom: "1px solid #e5e7eb",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              style={{
                padding: "4px 8px",
                fontSize: 11,
                borderRadius: 4,
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
              }}
              onClick={() => onChange([])}
            >
              Clear
            </button>
            <button
              type="button"
              style={{
                padding: "4px 8px",
                fontSize: 11,
                borderRadius: 4,
                border: "1px solid #111827",
                background: "#111827",
                color: "#fff",
                cursor: "pointer",
              }}
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>

          <div
            style={{
              margin: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            {options.length === 0 ? (
              <div
                style={{
                  padding: "8px 10px",
                  fontSize: 12,
                  color: "#6b7280",
                }}
              >
                No values in this list
              </div>
            ) : (
              options.map((opt) => (
                <label
                  key={opt.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 10px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f3f4f6",
                    fontSize: 12,
                    lineHeight: 1.3,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value.includes(opt.id)}
                    onChange={() => toggle(opt.id)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
